import { Slide } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import { TransitionProps } from '@mui/material/transitions';
import DestinationSetup from 'js/components/destination/DestinationSetupContainer';
import React from 'react';
import flowTypes from 'new/components/flows/types/flowTypes';
import CreateReplicationFlow from 'new/components/flows/types/replication/CreateReplicationFlow';
import CreateSparkFlow from 'new/components/flows/types/spark/CreateSparkFlow';
import CreateELTFlow from 'new/components/flows/types/elt/CreateELTFlow';
import CreateDirectFlow from 'new/components/flows/types/DirectFlow/CreateDirectFlow';
import CreateCDCFlow from 'new/components/flows/types/cdc/CreateCDCFlow';
import { ByoDestinationSetupForm } from 'new/components/flows/types/byo/v2-shells/canvas/ByoDestinationSetupForm';
import { UserConfirmationForModals } from 'new/components/UserConfirmationForModals';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';

import s from './ModalDestinationForm.module.scss';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

type Props = {
  id?: number;
  nexsetId?: number;
  isEditMode?: boolean;
  onDismiss?: (nexset: any) => void;
  onClose?: () => void;
  open: boolean;
  flowType?: keyof typeof flowTypes;
  queryParams?: Record<string, any>;
};

const flowTypeComponentMap = {
  streaming: DestinationSetup,
  replication: CreateReplicationFlow,
  spark: CreateSparkFlow,
  elt: CreateELTFlow,
  in_memory: CreateDirectFlow,
  custom: CreateDirectFlow,
  cdc: CreateCDCFlow,
  api_server: CreateDirectFlow,
};

const handleDialogClick = (event: React.MouseEvent) => {
  event?.stopPropagation();
};

const ModalDestinationForm = (props: Props) => {
  const { id, nexsetId, isEditMode, onDismiss, open, flowType = 'streaming', queryParams } = props;
  // See the matching comment in ModalSourceForm.tsx — gates ALL of the v2 BYO
  // edit UI, not just the create entry point.
  const byoV2Enabled = useFeatureFlag('UI_ENABLE_BYO_V2');

  const onClose = (event = null, reason = '') => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    if (props.onClose) {
      setTimeout(() => {
        // Why inside a timeout? To keep the animation working
        props.onClose();
      }, 500);
    }
  };

  const onDismissForm = (nexset: any) => {
    onClose();
    onDismiss?.(nexset);
  };

  if (flowType === 'custom' && byoV2Enabled) {
    return (
      <>
        <Dialog
          fullScreen
          keepMounted={false}
          open={open}
          onClose={onClose}
          PaperProps={{ style: { background: 'var(--color-white, #fff)', padding: 0 } }}
          TransitionComponent={Transition}
        >
          <ByoDestinationSetupForm
            id={id}
            nexsetId={nexsetId}
            sourceId={queryParams?.sourceId}
            isEditMode={isEditMode}
            onDismiss={(data) => onDismissForm(data)}
          />
        </Dialog>
        <UserConfirmationForModals open={open} onConfirm={() => onDismissForm(null)} />
      </>
    );
  }

  const DestinationComponent = flowTypeComponentMap[flowType];

  return (
    <>
      <Dialog
        keepMounted={false}
        open={open}
        onClose={onClose}
        TransitionComponent={Transition}
        fullWidth
        onClick={handleDialogClick}
        className={s.dialogContainerOverride}
      >
        <DestinationComponent
          isModal
          id={id}
          nexsetId={nexsetId}
          queryParams={queryParams}
          flowType={flowType}
          isEditMode={isEditMode}
          resourceType="sink"
          onDismiss={onDismissForm}
        />
      </Dialog>
      <UserConfirmationForModals open={open} onConfirm={() => onDismissForm(null)} />
    </>
  );
};

export default ModalDestinationForm;
